import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import he from "he";
import { animate } from "animejs";
import { closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const isVoided = (item) => !!item?.isVoided || Number(item?.TransType) === 2 || String(item?.status || "").toLowerCase() === "voided";

function isVisibleAddonItem(item) {
  const isAddon = item?.isAddon === true || item?.is_addon === true || item?.is_addon === 1 || item?.is_addon === "1" || item?.isaddon === true || item?.isaddon === 1 || item?.isaddon === "1";
  return isAddon && item?.suppressAddonDisplay !== true;
}

function getCookSeconds(item) {
  const cookSeconds = Number(item?.cookTimeSeconds);
  if (Number.isFinite(cookSeconds) && cookSeconds > 0) return Math.round(cookSeconds);
  const minutes = Number(item?.preparationTime ?? item?.PreparationTime ?? 0);
  return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes * 60) : null;
}

function isCookTimingCandidate(item) {
  if (!item || item?.isCourseSeparator || item?.isMessage || item?.isFireItem) return false;
  if (item?.isBumped || item?.isParked || isVoided(item)) return false;
  const status = String(item?.status || "").toLowerCase();
  if (status === "bumped" || status === "parked") return false;
  if (item?.prepReadyAt || item?.cookTimingStartedManually) return false;
  if (isVisibleAddonItem(item)) return false;
  return getCookSeconds(item) != null;
}

function getCookStartDelaySeconds(item, allItems = []) {
  if (!isCookTimingCandidate(item)) return 0;
  const itemCookSeconds = getCookSeconds(item);
  if (itemCookSeconds == null) return 0;
  const course = Number.isFinite(Number(item?.course)) ? Number(item.course) : 0;
  const maxCookSeconds = (allItems || []).reduce((max, candidate) => {
    if (Number(candidate?.course ?? 0) !== course || !isCookTimingCandidate(candidate)) return max;
    const cookSeconds = getCookSeconds(candidate);
    return cookSeconds == null ? max : Math.max(max, cookSeconds);
  }, 0);
  return Math.max(0, maxCookSeconds - itemCookSeconds);
}

function getPrepSignedSeconds(item, nowMs = Date.now(), allItems = []) {
  const prepSeconds = getCookStartDelaySeconds(item, allItems);
  if (!prepSeconds) return 0;
  const createdAt = new Date(item?.createdAt);
  if (Number.isNaN(createdAt.getTime())) return 0;
  const endAt = item?.isParked && item?.parkedAt ? new Date(item.parkedAt).getTime() : nowMs;
  const elapsedSeconds = Math.max(0, Math.floor((endAt - createdAt.getTime() - (Number(item?.totalPausedSeconds) || 0) * 1000) / 1000));
  return elapsedSeconds - prepSeconds;
}

function isPrepItem(item, nowMs = Date.now(), allItems = []) {
  if (!item || item?.isCourseSeparator || item?.isMessage || item?.isFireItem) return false;
  if (item?.isBumped || isVoided(item)) return false;
  const status = String(item?.status || "").toLowerCase();
  if (status === "bumped" || status === "parked" || status === "voided") return false;
  return getPrepSignedSeconds(item, nowMs, allItems) < 0;
}

function ItemTimer({ item, allItems = [] }) {
  const [seconds, setSeconds] = useState(0);
  const prepSeconds = getCookStartDelaySeconds(item, allItems);
  useEffect(() => {
    const update = () => {
      const createdAt = new Date(item?.createdAt);
      if (Number.isNaN(createdAt.getTime())) return;
      const endAt = item?.isParked && item?.parkedAt ? new Date(item.parkedAt) : new Date();
      const elapsedSeconds = Math.max(0, Math.floor((endAt.getTime() - createdAt.getTime() - (Number(item?.totalPausedSeconds) || 0) * 1000) / 1000));
      setSeconds(elapsedSeconds - prepSeconds);
    };
    update();
    if (item?.isParked) return undefined;
    const interval = window.setInterval(update, 1000);
    return () => window.clearInterval(interval);
  }, [item?.createdAt, item?.isParked, item?.parkedAt, item?.totalPausedSeconds, prepSeconds]);
  const sign = seconds < 0 ? "-" : "";
  const absSeconds = Math.abs(seconds);
  return `${sign}${Math.floor(absSeconds / 60)}:${String(absSeconds % 60).padStart(2, "0")}`;
}

function ModifierMarquee({ item }) {
  const containerRef = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const [durationSeconds, setDurationSeconds] = useState(28);
  const names = (Array.isArray(item?.Condiment) ? item.Condiment : item?.Condiment ? [item.Condiment] : []).map((condiment) => he.decode(String(condiment?.Name || "").trim())).filter(Boolean);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const update = () => {
      const isOverflowing = node.scrollWidth > node.clientWidth + 1;
      setOverflows(isOverflowing);
      if (isOverflowing) {
        const travelWidth = Math.max(node.scrollWidth / 2, node.clientWidth);
        setDurationSeconds(Math.max(32, Math.ceil(travelWidth / 18)));
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [names.join("|")]);
  if (!names.length) return <span className="text-gray-400 dark:text-gray-500">-</span>;
  const text = names.join("     ");
  return <div ref={containerRef} className="overflow-hidden whitespace-nowrap"><span style={overflows ? { animationDuration: `${durationSeconds}s` } : undefined} className={overflows ? "line-item-marquee inline-block" : "inline-block"}>{overflows ? `${text}     ${text}` : text}</span></div>;
}

function SortableLineItem({ row, children, setNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  return <div ref={(node) => { setNodeRef(node); setNode(row.id, node); }} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.3 : 1, zIndex: isDragging ? 2 : 1 }} className="touch-pan-y">
    {children({ ...attributes, ...listeners, isDragging })}
  </div>;
}

export default function LineItemBoard({ orders, settings, onBumpItem, onPrepItemReady, rowOrder = [], onRowOrderChange, isActionMode = false, onSelectRow }) {
  const lastTapRef = useRef(new Map());
  const rowNodesRef = useRef(new Map());
  const previousPositionsRef = useRef(new Map());
  const isScrollingRef = useRef(false);
  const scrollStopTimerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [bumpingId, setBumpingId] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 8 } }), useSensor(TouchSensor, { activationConstraint: { delay: 260, tolerance: 6 } }));
  const uiScale = {
    small: { header: "0.75rem", body: "0.875rem", item: "1rem", rowHeight: 72 },
    medium: { header: "0.875rem", body: "1rem", item: "1.125rem", rowHeight: 88 },
    large: { header: "1rem", body: "1.25rem", item: "1.375rem", rowHeight: 104 },
    extralarge: { header: "1.125rem", body: "1.5rem", item: "1.625rem", rowHeight: 120 },
    test: { header: "1.5rem", body: "2rem", item: "2.25rem", rowHeight: 148 },
  }[settings?.uiscale || "medium"] || { header: "0.875rem", body: "1rem", item: "1.125rem", rowHeight: 88 };
  const itemPrepDisplayMode = ["normal", "darkened", "hidden"].includes(String(settings?.item_prep_display_mode || "").toLowerCase()) ? String(settings.item_prep_display_mode).toLowerCase() : "normal";
  const defaultRows = useMemo(() => orders.flatMap((order) => (order?.items || []).filter((item) => !item?.isCourseSeparator && !item?.isMessage && !item?.isFireItem && !item?.isBumped && (itemPrepDisplayMode !== "hidden" || !isPrepItem(item, nowMs, order?.items || []))).map((item) => ({ id: `${order.ticket_instance_id}::${item.ID}`, order, item }))).sort((left, right) => {
    const leftWaiting = isPrepItem(left.item, nowMs, left.order?.items || []);
    const rightWaiting = isPrepItem(right.item, nowMs, right.order?.items || []);
    if (leftWaiting !== rightWaiting) return leftWaiting ? 1 : -1;
    const earliest = (order) => Math.min(...(order?.items || []).map((item) => new Date(item?.createdAt || 0).getTime()).filter(Number.isFinite));
    return earliest(left.order) - earliest(right.order) || new Date(left.item?.createdAt || 0).getTime() - new Date(right.item?.createdAt || 0).getTime();
  }), [orders, itemPrepDisplayMode, nowMs]);
  const rows = useMemo(() => {
    const byId = new Map(defaultRows.map((row) => [row.id, row]));
    const saved = rowOrder.map((id) => byId.get(id)).filter(Boolean);
    const savedIds = new Set(saved.map((row) => row.id));
    return saved.concat(defaultRows.filter((row) => !savedIds.has(row.id)));
  }, [defaultRows, rowOrder]);
  const rowOrderSignature = rows.map((row) => row.id).join("|");

  useLayoutEffect(() => {
    const currentPositions = new Map();
    rowNodesRef.current.forEach((node, id) => {
      if (!node) return;
      const top = node.getBoundingClientRect().top;
      currentPositions.set(id, top);
      const previousTop = previousPositionsRef.current.get(id);
      const deltaY = previousTop == null ? 0 : previousTop - top;
      if (deltaY && !activeId && !isScrollingRef.current) {
        animate(node, { translateY: [deltaY, 0], duration: 380, ease: "outQuad" });
      }
    });
    previousPositionsRef.current = currentPositions;
  }, [rowOrderSignature, activeId]);

  useEffect(() => () => window.clearTimeout(scrollStopTimerRef.current), []);
  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);
  const handleScroll = () => {
    isScrollingRef.current = true;
    window.clearTimeout(scrollStopTimerRef.current);
    scrollStopTimerRef.current = window.setTimeout(() => {
      isScrollingRef.current = false;
    }, 140);
  };

  const getCourseLabel = (item) => {
    const course = Number(item?.course);
    if (!Number.isFinite(course)) return "Course";
    return settings?.course_labels?.[String(course)] || settings?.course_labels?.[String(course + 1)] || `Course ${course + 1}`;
  };
  const handleTap = (row) => {
    if (activeId || bumpingId) return;
    if (isActionMode) {
      onSelectRow?.(row);
      return;
    }
    if (isVoided(row.item)) return;
    const now = Date.now();
    if (now - (lastTapRef.current.get(row.id) || 0) < 320) {
      lastTapRef.current.delete(row.id);
      if (itemPrepDisplayMode === "darkened" && isPrepItem(row.item, nowMs, row.order?.items || [])) {
        onPrepItemReady?.(row.order.ticket_instance_id, row.item.ID);
        return;
      }
      setBumpingId(row.id);
      const node = rowNodesRef.current.get(row.id);
      if (!node) {
        onBumpItem(row.order.ticket_instance_id, row.item.ID);
        setBumpingId(null);
        return;
      }
      animate(node, {
        scale: [1, 1.018, 0.985],
        opacity: [1, 1, 0],
        duration: 230,
        ease: "outQuad",
        onComplete: () => {
          onBumpItem(row.order.ticket_instance_id, row.item.ID);
          setBumpingId(null);
        },
      });
      return;
    }
    lastTapRef.current.set(row.id, now);
  };
  return <section onScroll={handleScroll} className={`scrollbar-hide h-full overflow-y-auto bg-gray-100 dark:bg-gray-900 ${isActionMode ? "relative z-50" : ""}`}>
    <style>{`@keyframes line-item-marquee { 0%, 18% { transform: translateX(0); } 100% { transform: translateX(-50%); } } .line-item-marquee { animation: line-item-marquee 32s linear infinite; }`}</style>
    <div style={{ width: "calc(100% - 50px)" }} className="min-h-full min-w-[850px] border-r border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div style={{ fontSize: uiScale.header }} className="sticky top-0 z-10 grid grid-cols-[120px_160px_minmax(240px,1.4fr)_minmax(280px,2fr)_120px] bg-gray-200 font-bold uppercase tracking-wide text-gray-700 shadow-sm dark:bg-gray-700 dark:text-gray-100"><div className="border-r border-gray-300 px-4 py-4 dark:border-gray-600">Ticket #</div><div className="border-r border-gray-300 px-4 py-4 dark:border-gray-600">Course</div><div className="border-r border-gray-300 px-4 py-4 dark:border-gray-600">Item Name</div><div className="border-r border-gray-300 px-4 py-4 dark:border-gray-600">Item Modifiers</div><div className="px-4 py-4">Item Timer</div></div>
      {rows.length === 0 ? <div className="p-10 text-center text-base text-gray-500 dark:text-gray-400">No line items to display.</div> : <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[({ transform }) => ({ ...transform, x: 0 })]} onDragStart={({ active }) => setActiveId(active.id)} onDragCancel={() => setActiveId(null)} onDragEnd={({ active, over }) => { if (active.id !== over?.id) { const oldIndex = rows.findIndex((row) => row.id === active.id); const newIndex = rows.findIndex((row) => row.id === over?.id); if (oldIndex >= 0 && newIndex >= 0) onRowOrderChange?.(arrayMove(rows, oldIndex, newIndex).map((row) => row.id)); } setActiveId(null); }}><SortableContext items={rows.map((row) => row.id)} strategy={verticalListSortingStrategy}>{rows.map((row) => {
        const voided = isVoided(row.item);
        const fired = row.item?.isFired || String(row.item?.status || "").toLowerCase() === "fired";
        const inPrep = itemPrepDisplayMode === "darkened" && isPrepItem(row.item, nowMs, row.order?.items || []);
        const rowClass = voided ? "bg-gray-500/35 text-gray-500 line-through dark:bg-gray-950/70 dark:text-gray-400" : fired ? "bg-red-200 text-red-950 dark:bg-red-950/70 dark:text-red-100" : inPrep ? "bg-gray-200 text-gray-600 opacity-45 grayscale dark:bg-gray-950 dark:text-gray-400" : "bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100";
        return <SortableLineItem key={row.id} row={row} setNode={(id, node) => node ? rowNodesRef.current.set(id, node) : rowNodesRef.current.delete(id)}>{({ isDragging, ...dragProps }) => <div {...dragProps} onClick={() => handleTap(row)} style={{ minHeight: uiScale.rowHeight, fontSize: uiScale.body }} className={`grid grid-cols-[120px_160px_minmax(240px,1.4fr)_minmax(280px,2fr)_120px] border-b border-gray-200 px-0 select-none transition-shadow active:cursor-grabbing dark:border-gray-700 ${rowClass} ${isActionMode ? "ring-4 ring-inset ring-yellow-400 shadow-[inset_0_0_0_1px_rgba(161,98,7,0.45)]" : ""} ${isDragging ? "shadow-2xl" : ""} ${bumpingId === row.id ? "pointer-events-none" : ""}`} title={isActionMode ? "Select this ticket" : voided ? "Voided item" : inPrep ? "Double-tap to start cooking this item now. Drag up or down to reorder." : "Double-tap to bump item. Drag up or down to reorder."}><div className="flex items-center border-r border-gray-200 px-4 font-semibold dark:border-gray-700">{row.order?.order_id ?? row.order?.ticket_instance_id}</div><div className="flex items-center border-r border-gray-200 px-4 dark:border-gray-700">{getCourseLabel(row.item)}</div><div style={{ fontSize: uiScale.item }} className="flex items-center border-r border-gray-200 px-4 font-semibold dark:border-gray-700">{he.decode(String(row.item?.Name || ""))}{voided ? " (VOIDED)" : ""}</div><div className="flex items-center overflow-hidden border-r border-gray-200 px-4 dark:border-gray-700"><ModifierMarquee item={row.item} /></div><div style={{ fontSize: uiScale.item }} className="flex items-center px-4 font-mono tabular-nums">{voided ? "-" : <ItemTimer item={row.item} allItems={row.order?.items || []} />}</div></div>}</SortableLineItem>;
      })}</SortableContext></DndContext>}
    </div>
  </section>;
}
